import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';
import { Bell, CalendarDays, CheckSquare, Package, ChevronRight, UserX, MessageCircle } from 'lucide-react';

export default function NotificationBell() {
  const [digest, setDigest] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const localDate = new Date().toLocaleDateString('en-CA');
        const r = await api.get(`/briefy/daily-digest?date=${localDate}`);
        if (!mounted) return;
        setDigest(r.data);
        if (localStorage.getItem('bc_digest_shown') !== localDate) {
          localStorage.setItem('bc_digest_shown', localDate);
          const c = r.data?.counts || {};
          const total = (c.appointments || 0) + (c.tasks || 0) + (c.low_stock || 0);
          if (total > 0) {
            toast.message(`☀️ ${t('digest.toastTitle', 'Briefy — Tu día de hoy')}`, {
              description: `${c.appointments || 0} ${t('digest.appointments', 'citas')} · ${c.tasks || 0} ${t('digest.tasks', 'tareas')} · ${c.low_stock || 0} ${t('digest.lowStock', 'con stock bajo')}`,
              duration: 9000,
            });
          }
        }
      } catch { /* silent */ }
    };
    load();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const c = digest?.counts || {};
  const inactive = digest?.inactive_customers || [];
  const badge = (c.appointments || 0) + (c.tasks || 0) + (c.low_stock || 0) + inactive.length;

  const Section = ({ icon: Icon, tint, title, items, empty, route, render }) => (
    <div className="px-1">
      <button
        onClick={() => { setOpen(false); navigate(route); }}
        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[#F1F5F9] transition-colors"
      >
        <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${tint.bg}`}>
          <Icon className={`w-3.5 h-3.5 ${tint.fg}`} strokeWidth={2} />
        </span>
        <span className="text-xs font-bold text-[#0F172A] uppercase tracking-wider flex-1 text-left">{title}</span>
        <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
      </button>
      {items.length === 0 ? (
        <p className="text-xs text-[#94A3B8] px-11 pb-2">{empty}</p>
      ) : (
        <div className="pl-11 pr-2 pb-2 space-y-1">
          {items.slice(0, 3).map((it, i) => (
            <p key={i} className="text-xs text-[#475569] truncate">{render(it)}</p>
          ))}
          {items.length > 3 && <p className="text-[11px] text-[#2563EB] font-semibold">+{items.length - 3} {t('digest.more', 'más')}</p>}
        </div>
      )}
    </div>
  );

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        data-testid="notification-bell-btn"
        className="relative p-2 text-slate-300 hover:text-white transition-colors rounded-lg hover:bg-white/10"
      >
        <Bell className="w-5 h-5" strokeWidth={1.9} />
        {badge > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#EF4444] text-white text-[9px] font-bold flex items-center justify-center" data-testid="notification-badge">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-[320px] bg-white border border-[#E2E8F0] rounded-2xl shadow-xl z-50 overflow-hidden" data-testid="notification-panel">
            <div className="flex items-center gap-2.5 px-4 py-3 border-b border-[#E2E8F0] bg-[#F8FAFC]">
              <div className="w-8 h-8 rounded-full bg-white p-0.5 overflow-hidden ring-2 ring-[#2563EB]/40 shrink-0">
                <img src="/mascot.png" alt="Briefy" className="w-full h-full object-contain" />
              </div>
              <div className="leading-tight">
                <p className="text-sm font-bold text-[#0F172A]">{t('digest.title', 'Resumen de hoy')}</p>
                <p className="text-[11px] text-[#64748B]">{t('digest.byBriefy', 'Preparado por Briefy')}</p>
              </div>
            </div>
            <div className="py-2 max-h-[380px] overflow-y-auto">
              <Section
                icon={CalendarDays}
                tint={{ bg: 'bg-[#DBEAFE]', fg: 'text-[#2563EB]' }}
                title={`${t('digest.appointmentsTitle', 'Citas de hoy')} (${c.appointments || 0})`}
                items={digest?.appointments || []}
                empty={t('digest.noAppointments', 'Sin citas para hoy')}
                route="/appointments"
                render={(a) => `${a.time ? a.time + ' · ' : ''}${a.title || a.customer_name || ''}`}
              />
              <Section
                icon={CheckSquare}
                tint={{ bg: 'bg-[#DCFCE7]', fg: 'text-[#16A34A]' }}
                title={`${t('digest.tasksTitle', 'Tareas de hoy')} (${c.tasks || 0})`}
                items={digest?.tasks || []}
                empty={t('digest.noTasks', 'Sin tareas pendientes hoy')}
                route="/tasks"
                render={(tk) => `${tk.time ? tk.time + ' · ' : ''}${tk.title}`}
              />
              <Section
                icon={Package}
                tint={{ bg: 'bg-[#FEE2E2]', fg: 'text-[#DC2626]' }}
                title={`${t('digest.stockTitle', 'Stock bajo')} (${c.low_stock || 0})`}
                items={digest?.low_stock || []}
                empty={t('digest.noLowStock', 'Inventario saludable')}
                route="/inventory"
                render={(p) => `${p.name} — ${p.stock} ${t('digest.units', 'uds')}`}
              />
              {/* Inactive customers — Briefy "come back" reminders */}
              <div className="px-1" data-testid="digest-inactive-section">
                <div className="w-full flex items-center gap-2 px-2 py-1.5">
                  <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#FEF3C7]">
                    <UserX className="w-3.5 h-3.5 text-[#D97706]" strokeWidth={2} />
                  </span>
                  <span className="text-xs font-bold text-[#0F172A] uppercase tracking-wider flex-1 text-left">
                    {t('digest.inactiveTitle', 'Clientes sin regresar')} ({inactive.length})
                  </span>
                </div>
                {inactive.length === 0 ? (
                  <p className="text-xs text-[#94A3B8] px-11 pb-2">{t('digest.noInactive', 'Todos tus clientes están activos')}</p>
                ) : (
                  <div className="pl-11 pr-2 pb-2 space-y-1.5">
                    {inactive.slice(0, 4).map((cst) => (
                      <div key={cst.id} className="flex items-center gap-1.5">
                        <p className="text-xs text-[#475569] truncate flex-1">
                          {cst.name} · <span className="text-[#D97706] font-semibold">{cst.days_inactive} {t('digest.daysShort', 'días')}</span>
                        </p>
                        {cst.phone && (
                          <a
                            href={`https://wa.me/${String(cst.phone).replace(/\D/g, '')}?text=${encodeURIComponent(t('digest.waComeback', '¡Hola! Te extrañamos 😊 ¿Te gustaría agendar tu próxima visita?'))}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1 rounded hover:bg-green-500/10 text-green-600"
                            title="WhatsApp"
                            data-testid={`inactive-wa-${cst.id}`}
                          >
                            <MessageCircle className="w-3.5 h-3.5" />
                          </a>
                        )}
                        <button
                          onClick={() => {
                            setOpen(false);
                            window.dispatchEvent(new CustomEvent('briefy:open', { detail: { prefill: `Sin crear ni modificar ningún registro, redáctame un mensaje corto y cálido para invitar a volver a mi cliente «${cst.name}», que lleva ${cst.days_inactive} días sin comprar ni agendar cita.` } }));
                          }}
                          className="p-0.5 rounded hover:bg-blue-500/10 shrink-0"
                          title={t('digest.draftMsg', 'Pedir mensaje a BRIEFY')}
                          data-testid={`inactive-briefy-${cst.id}`}
                        >
                          <img src="/mascot.png" alt="" style={{ width: '18px', height: '18px' }} className="object-contain" />
                        </button>
                      </div>
                    ))}
                    {inactive.length > 4 && <p className="text-[11px] text-[#2563EB] font-semibold">+{inactive.length - 4} {t('digest.more', 'más')}</p>}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
